import React, { useState, useEffect } from 'react';
import { Row, Col, Select, Button, Modal, Form, Input, Upload, List, Typography, message } from 'antd';
import { bannerReleaseService } from 'src/services/banner-release';
import { PlusOutlined, MenuOutlined, CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import moment from 'moment';

const { Option } = Select;
const { Title } = Typography;

const arrayMove = (array, from, to) => {
    const newArray = array.slice();
    newArray.splice(to < 0 ? newArray.length + to : to, 0, newArray.splice(from, 1)[0]);
    return newArray;
};

const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: 'grab', color: '#999', marginRight: 10 }} />);

const SortableItem = SortableElement(({ value, onClose }) => (
    <List.Item style={{ background: '#fff', marginBottom: 8, padding: '10px', border: '1px solid #eee', borderRadius: 4, display: 'flex', alignItems: 'center' }}>
        <DragHandle />
        <span style={{ flex: 1 }}>{value.alt || '未命名'}</span>
        <Button type="text" icon={<CloseOutlined />} danger onClick={() => onClose(value.id)} />
    </List.Item>
));

const SortableList = SortableContainer(({ items, onClose }) => {
    return (
        <List
            dataSource={items}
            renderItem={(item, index) => (
                <SortableItem index={index} key={item.id} value={item} onClose={onClose} />
            )}
        />
    );
});

const GrayBox = ({ text, height = 200, onClick, image }) => (
    <div
        onClick={onClick}
        style={{
            backgroundColor: '#e0e0e0',
            height: height,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            border: '1px solid #ccc',
            position: 'relative',
            overflow: 'hidden'
        }}
    >
        {image ? (
            <img src={image} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
            <div style={{ fontSize: 24, color: '#000' }}>+</div>
        )}
    </div>
);

const RootNewOfficialBanner = () => {
    const [adType, setAdType] = useState('HOME_BANNER'); 
    const [currentTime, setCurrentTime] = useState('');
    const [lastReleasedTime, setLastReleasedTime] = useState(null);
    
    // HOME_BANNER data (Carousel)
    const [banners, setBanners] = useState([]);
    const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

    // Static Banners data (For HOME_MAIN, JET_MAIN, PRODUCT_INS)
    const [staticBanners, setStaticBanners] = useState({});

    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isSortModalVisible, setIsSortModalVisible] = useState(false);
    const [editForm] = Form.useForm();
    const [currentEditId, setCurrentEditId] = useState(null);
    const [currentEditType, setCurrentEditType] = useState('BANNER'); // 'BANNER' or 'STATIC'
    const [currentEditKey, setCurrentEditKey] = useState(null); // e.g. 'hm_1'
    const [isSaving, setIsSaving] = useState(false);

    const getBannerTypeId = (type) => {
        switch(type) {
            case 'HOME_BANNER': return 1;
            case 'MERCHANT_BANNER': return 2;
            case 'HOME_MAIN': return 3;
            case 'JET_MAIN': return 4;
            case 'PRODUCT_INS': return 5;
            default: return 1;
        }
    };

    const fetchBanners = React.useCallback(async () => {
        try {
            const data = await bannerReleaseService.getDraftBanners(getBannerTypeId(adType));
            
            if (data.lastReleasedAt) {
                setCurrentTime(moment(data.lastReleasedAt).format('YYYY/MM/DD HH:mm:ss'));
                setLastReleasedTime(data.lastReleasedAt);
            } else {
                setCurrentTime('尚未發佈');
                setLastReleasedTime(null);
            }

            const list = data.banners || [];
            if (adType === 'HOME_BANNER' || adType === 'MERCHANT_BANNER') {
                setBanners(list.map(b => ({
                    id: b.bannerNo,
                    alt: b.bannerDescription,
                    link: b.bannerUrl,
                    desktopImg: b.bannerDocImg,
                    mobileImg: b.bannerMobileImg,
                    position: b.bannerPosition,
                    updatedAt: b.updatedAt,
                    isPublished: b.isPublished
                })));
                if (list.length > 0 && currentBannerIndex >= list.length) {
                    setCurrentBannerIndex(0);
                }
            } else {
                let prefix = '';
                if (adType === 'HOME_MAIN') prefix = 'hm_';
                else if (adType === 'JET_MAIN') prefix = 'jet_';
                else if (adType === 'PRODUCT_INS') prefix = 'pi_';

                const map = {};

                const positionToKeyMap = {
                    'index-left': 'hm_1',
                    'index-right-left': 'hm_2',
                    'index-right-right': 'hm_3',
                    'index-right-bottom': 'hm_4',
                    'jetdai-left': 'jet_1',
                    'jetdai-right-left': 'jet_2',
                    'jetdai-right-right': 'jet_3',
                    'jetdai-right-bottom': 'jet_4',
                    'installment-left': 'pi_1',
                    'installment-right-top': 'pi_2',
                    'installment-right-middle': 'pi_3',
                    'installment-right-bottom': 'pi_4',
                };

                list.forEach(b => {
                    let key = null;
                    if (positionToKeyMap[b.bannerPosition]) {
                        key = positionToKeyMap[b.bannerPosition];
                    } else if (b.bannerPosition) {
                        key = `${prefix}${b.bannerPosition}`;
                    } else if (b.bannerOrder) {
                        key = `${prefix}${b.bannerOrder}`;
                    }

                    if (key) {
                        map[key] = {
                            id: b.bannerNo,
                            alt: b.bannerDescription,
                            link: b.bannerUrl,
                            desktopImg: b.bannerDocImg,
                            mobileImg: b.bannerMobileImg,
                            position: b.bannerPosition,
                            updatedAt: b.updatedAt,
                            isPublished: b.isPublished
                        };
                    }
                });
                setStaticBanners(map);
            }
        } catch (error) {
            message.error('取得失敗:' + error);
        }
    }, [adType]);

    useEffect(() => {
        // Reset index only when adType changes
        setCurrentBannerIndex(0);
        fetchBanners();
    }, [fetchBanners]);

    const onSortEnd = async ({ oldIndex, newIndex }) => {
        if (oldIndex === newIndex) return;
        const newBanners = arrayMove(banners, oldIndex, newIndex);
        setBanners(newBanners);
        
        try {
            const bannerNos = newBanners.map(b => b.id);
            await bannerReleaseService.updateBannerOrder(getBannerTypeId(adType), bannerNos);
            message.success('排序已更新');
            fetchBanners();
        } catch (error) {
            message.error('排序更新失敗');
            fetchBanners(); 
        }
    };

    const handleAddBanner = () => {
        const tempId = `new_${Date.now()}`;
        const newBanner = { 
            id: tempId, 
            alt: '', 
            link: '', 
            desktopImg: null, 
            mobileImg: null,
            position: banners.length + 1
        };
        setBanners([...banners, newBanner]);
        setCurrentBannerIndex(banners.length);
        message.success('已新增廣告，請點擊灰色區塊進行編輯');
    };

    const handleEditBanner = (index) => {
        const banner = banners[index];
        if(!banner) return;
        setCurrentEditId(banner.id);
        setCurrentEditType('BANNER');
        
        const toFileList = (url) => url ? [{ uid: '-1', name: 'image.png', status: 'done', url: url }] : [];
        
        const isNew = String(banner.id).startsWith('new_');
        const status = (isNew || banner.isPublished === false) ? '未發佈' : '已發佈';

        editForm.setFieldsValue({
            status: status,
            alt: banner.alt,
            link: banner.link,
            desktopImg: toFileList(banner.desktopImg),
            mobileImg: toFileList(banner.mobileImg)
        });
        setIsAddModalVisible(true);
    }

    const handleEditStatic = (key) => {
        const data = staticBanners[key] || {};
        setCurrentEditId(data.id || null);
        setCurrentEditType('STATIC');
        setCurrentEditKey(key);
        
        const toFileList = (url) => url ? [{ uid: '-1', name: 'image.png', status: 'done', url: url }] : [];

        const isNew = !data.id || String(data.id).startsWith('new_');
        const status = (isNew || data.isPublished === false) ? '未發佈' : '已發佈';

        editForm.setFieldsValue({
            status: status,
            alt: data.alt,
            link: data.link,
            desktopImg: toFileList(data.desktopImg),
            mobileImg: toFileList(data.mobileImg)
        });
        setIsAddModalVisible(true);
    };
    
    const normFile = (e) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e && e.fileList;
    };
    
    const customUpload = (file, onSuccess) => {
        setTimeout(() => {
            onSuccess("ok");
        }, 0);
    };

    const handleSave = () => {
        editForm.validateFields().then(async values => {
            setIsSaving(true);
            try {
                const formData = new FormData();
                formData.append('bannerType', getBannerTypeId(adType));
                if (values.alt) formData.append('bannerDescription', values.alt);
                if (values.link) formData.append('bannerUrl', values.link);
                
                // Add bannerPosition based on currentEditKey (for Static) or existing logic
                if (currentEditType === 'STATIC' && currentEditKey) {
                    const positionMap = {
                        // HOME_MAIN
                        'hm_1': 'index-left',
                        'hm_2': 'index-right-left',
                        'hm_3': 'index-right-right',
                        'hm_4': 'index-right-bottom',
                        // JET_MAIN
                        'jet_1': 'jetdai-left',
                        'jet_2': 'jetdai-right-left',
                        'jet_3': 'jetdai-right-right',
                        'jet_4': 'jetdai-right-bottom',
                        // PRODUCT_INS
                        'pi_1': 'installment-left',
                        'pi_2': 'installment-right-top',
                        'pi_3': 'installment-right-middle',
                        'pi_4': 'installment-right-bottom',
                    };
                    if (positionMap[currentEditKey]) {
                        formData.append('bannerPosition', positionMap[currentEditKey]);
                    }
                } else if (currentEditType === 'BANNER') {
                    const banner = banners.find(b => b.id === currentEditId);
                    if (banner && banner.position) {
                        formData.append('bannerPosition', banner.position);
                    }
                }

                if (currentEditId && !String(currentEditId).startsWith('new_')) {
                    formData.append('bannerNo', currentEditId);
                }
                
                let hasImg = false;
                if (values.desktopImg && values.desktopImg[0] && values.desktopImg[0].originFileObj) {
                    formData.append('webImg', values.desktopImg[0].originFileObj);
                    hasImg = true;
                }
                if (values.mobileImg && values.mobileImg[0] && values.mobileImg[0].originFileObj) {
                    formData.append('mobileImg', values.mobileImg[0].originFileObj);
                    hasImg = true;
                }

                const hasDesktop = values.desktopImg && values.desktopImg.length > 0;
                const hasMobile = values.mobileImg && values.mobileImg.length > 0;

                // If adding new (no ID or temp ID) and no image, check validation if API requires it.
                // An existing image (from fileList) is also valid, so we check hasDesktop/hasMobile.
                if ((!currentEditId || String(currentEditId).startsWith('new_')) && !hasImg && !hasDesktop && !hasMobile) {
                    setIsSaving(false);
                    return message.error('新增時請上傳圖片');
                }

                await bannerReleaseService.upsertDraftBanner(formData);
                message.success('儲存成功');
                setIsAddModalVisible(false);
                fetchBanners();
                
                const getImgUrl = (list, originalUrl) => {
                    if (list && list.length > 0) {
                        if (list[0].originFileObj) {
                            return URL.createObjectURL(list[0].originFileObj);
                        }
                        return list[0].url || originalUrl;
                    }
                    return originalUrl;
                };

                if (adType === 'HOME_BANNER' || adType === 'MERCHANT_BANNER') {
                    setBanners(prev => prev.map(b => {
                        if (b.id === currentEditId) {
                            return {
                                ...b,
                                alt: values.alt,
                                link: values.link,
                                desktopImg: getImgUrl(values.desktopImg, b.desktopImg),
                                mobileImg: getImgUrl(values.mobileImg, b.mobileImg),
                                updatedAt: moment().toISOString(),
                                isPublished: false
                            };
                        }
                        return b;
                    }));
                } else {
                    if (currentEditType === 'STATIC' && currentEditKey) {
                        setStaticBanners(prev => {
                            const old = prev[currentEditKey] || {};
                            return {
                                ...prev,
                                [currentEditKey]: {
                                    ...old,
                                    id: old.id || currentEditId, // Maintain ID just in case
                                    alt: values.alt,
                                    link: values.link,
                                    desktopImg: getImgUrl(values.desktopImg, old.desktopImg),
                                    mobileImg: getImgUrl(values.mobileImg, old.mobileImg),
                                    updatedAt: moment().toISOString(),
                                    isPublished: false
                                }
                            };
                        });
                    }
                }

            } catch(e) {
                message.error('儲存失敗:' + e);
            } finally {
                setIsSaving(false);
            }
        });
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: '確定刪除?',
            onOk: async () => {
                try {
                    await bannerReleaseService.deleteDraftBanner(id);
                    message.success('刪除成功');
                    fetchBanners();
                } catch (e) {
                    message.error('刪除失敗');
                }
            }
        });
    };
    
    const handlePublish = async () => {
        try {
            await bannerReleaseService.publishBanners();
            message.success('發佈成功');
            fetchBanners();
        } catch(e) {
            message.error('發佈失敗');
        }
    };
    
    const nextBanner = () => {
        if (banners.length === 0) return;
        setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    };

    const prevBanner = () => {
        if (banners.length === 0) return;
        setCurrentBannerIndex((prev) => (prev - 1 + banners.length) % banners.length);
    };

    const getRecSize = (isMobile) => {
        if (adType === 'HOME_BANNER') {
            return isMobile ? '1080 × 1350 px' : '1920 × 700 px';
        }
        if (currentEditType === 'STATIC' && currentEditKey) {
            const suffix = currentEditKey.split('_')[1];
            if (adType === 'HOME_MAIN' || adType === 'JET_MAIN') {
                if (suffix === '1') return isMobile ? '800 × 800 px' : '900 × 1080 px';
                if (suffix === '2') return isMobile ? '800 × 800 px' : '800 × 600 px';
                if (suffix === '3') return isMobile ? '800 × 800 px' : '800 × 600 px';
                if (suffix === '4') return isMobile ? '800 × 800 px' : '900 × 700 px';
            }
            if (adType === 'PRODUCT_INS') {
                if (suffix === '1') return isMobile ? '800 × 400 px' : '1200 × 600 px';
                if (['2', '3', '4'].includes(suffix)) return isMobile ? '800 × 400 px' : '600 × 300 px';
            }
        }
        return '768 × 500 px';
    };

    const renderUploadItem = (label, name) => (
        <Form.Item
            label={label}
            name={name}
            valuePropName="fileList"
            getValueFromEvent={normFile}
        >
            <Upload 
                listType="picture" 
                maxCount={1} 
                beforeUpload={() => false} 
                customRequest={({ file, onSuccess }) => customUpload(file, onSuccess)}
                accept="image/*"
            >
                <div>
                    <Button icon={<UploadOutlined />}>點擊上傳</Button>
                </div>
            </Upload>
        </Form.Item>
    );

    const renderContent = () => {
        // Mobile 4-grid helper
        const renderMobileGrid = (prefix, span = 6) => (
            <Row gutter={[16, 16]}>
            {[1, 2, 3, 4].map(i => {
                const id = `${prefix}_${i}`;
                return (
                    <Col span={span} key={id}>
                        <GrayBox height={150} onClick={() => handleEditStatic(id)} image={staticBanners[id]?.mobileImg} />
                    </Col>
                );
            })}
            </Row>
        );

        switch (adType) {
            case 'HOME_BANNER':
            case 'MERCHANT_BANNER':
                return (
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <Button type="primary" onClick={handleAddBanner} icon={<PlusOutlined />} style={{ marginRight: 8 }}>新增</Button>
                            <Button type="primary" onClick={() => setIsSortModalVisible(true)}>排序</Button>
                        </div>
                        <div style={{ marginBottom: 24 }}>
                            <Title level={5}>電腦版</Title>
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Button type="text" onClick={prevBanner} style={{ fontSize: 24, marginRight: 10 }}>&lt;</Button>
                                        <div style={{ flex: 1, position: 'relative', height: 300 }}>
                                            {banners.length > 0 ? banners.map((b, i) => (
                                                <div key={b.id || i} style={{ 
                                                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                                    opacity: i === currentBannerIndex ? 1 : 0,
                                                    zIndex: i === currentBannerIndex ? 2 : 1,
                                                    transition: 'opacity 0.3s',
                                                    pointerEvents: i === currentBannerIndex ? 'auto' : 'none'
                                                }}>
                                                    <GrayBox height={300} onClick={() => handleEditBanner(i)} image={b.desktopImg} />
                                                </div>
                                            )) : (
                                                <GrayBox height={300} onClick={() => {}} />
                                            )}
                                        </div>
                                        <Button type="text" onClick={nextBanner} style={{ fontSize: 24, marginLeft: 10 }}>&gt;</Button>
                                    </div>
                                    <div style={{textAlign: 'center', marginTop: 8}}>
                                        {banners.length > 0 ? (currentBannerIndex + 1) : 0} / {banners.length}
                                    </div>
                                </Col>
                            </Row>
                        </div>
                        <div>
                            <Title level={5}>手機版</Title>
                            <Row gutter={[16, 16]}>
                                <Col span={12} offset={6}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Button type="text" onClick={prevBanner} style={{ fontSize: 24, marginRight: 10 }}>&lt;</Button>
                                        <div style={{ flex: 1, position: 'relative', height: 200 }}>
                                            {banners.length > 0 ? banners.map((b, i) => (
                                                <div key={b.id || i} style={{ 
                                                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                                    opacity: i === currentBannerIndex ? 1 : 0,
                                                    zIndex: i === currentBannerIndex ? 2 : 1,
                                                    transition: 'opacity 0.3s',
                                                    pointerEvents: i === currentBannerIndex ? 'auto' : 'none'
                                                }}>
                                                    <GrayBox height={200} onClick={() => handleEditBanner(i)} image={b.mobileImg} />
                                                </div>
                                            )) : (
                                                <GrayBox height={200} onClick={() => {}} />
                                            )}
                                        </div>
                                        <Button type="text" onClick={nextBanner} style={{ fontSize: 24, marginLeft: 10 }}>&gt;</Button>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>
                );
            case 'HOME_MAIN':
                return ( // 首頁主視覺
                    <div>
                        <div style={{ marginBottom: 24 }}>
                            <Title level={5}>電腦版</Title>
                            <Row gutter={[8, 8]}>
                                <Col span={12}>
                                    <GrayBox height={408} onClick={() => handleEditStatic('hm_1')} image={staticBanners['hm_1']?.desktopImg} />
                                </Col>
                                <Col span={12}>
                                    <Row gutter={[8, 8]}>
                                        <Col span={12}>
                                            <GrayBox height={200} onClick={() => handleEditStatic('hm_2')} image={staticBanners['hm_2']?.desktopImg} />
                                        </Col>
                                        <Col span={12}>
                                            <GrayBox height={200} onClick={() => handleEditStatic('hm_3')} image={staticBanners['hm_3']?.desktopImg} />
                                        </Col>
                                        <Col span={24}>
                                            <GrayBox height={200} onClick={() => handleEditStatic('hm_4')} image={staticBanners['hm_4']?.desktopImg} />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </div>
                        <div>
                            <Title level={5}>手機版</Title>
                            {renderMobileGrid('hm', 12)}
                        </div>
                    </div>
                );
            case 'JET_MAIN': // Jet貸主視覺
                return ( 
                    <div>
                        <div style={{ marginBottom: 24 }}>
                            <Title level={5}>電腦版</Title>
                            <Row gutter={[8, 8]}>
                                <Col span={12}>
                                    <GrayBox height={408} onClick={() => handleEditStatic('jet_1')} image={staticBanners['jet_1']?.desktopImg} />
                                </Col>
                                <Col span={12}>
                                    <Row gutter={[8, 8]}>
                                        <Col span={12}>
                                            <GrayBox height={200} onClick={() => handleEditStatic('jet_2')} image={staticBanners['jet_2']?.desktopImg} />
                                        </Col>
                                        <Col span={12}>
                                            <GrayBox height={200} onClick={() => handleEditStatic('jet_3')} image={staticBanners['jet_3']?.desktopImg} />
                                        </Col>
                                        <Col span={24}>
                                            <GrayBox height={200} onClick={() => handleEditStatic('jet_4')} image={staticBanners['jet_4']?.desktopImg} />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </div>
                        <div>
                            <Title level={5}>手機版</Title>
                            {renderMobileGrid('jet', 12)}
                        </div>
                    </div>
                );
             case 'PRODUCT_INS': // 商品分期主視覺
                return (
                    <div>
                        <div style={{ marginBottom: 24 }}>
                            <Title level={5}>電腦版</Title>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <GrayBox height={600} onClick={() => handleEditStatic('pi_1')} image={staticBanners['pi_1']?.desktopImg} />
                                </Col>
                                <Col span={12}>
                                    <Row gutter={[16, 16]}>
                                        <Col span={24}><GrayBox height={189} onClick={() => handleEditStatic('pi_2')} image={staticBanners['pi_2']?.desktopImg} /></Col>
                                        <Col span={24}><GrayBox height={189} onClick={() => handleEditStatic('pi_3')} image={staticBanners['pi_3']?.desktopImg} /></Col>
                                        <Col span={24}><GrayBox height={190} onClick={() => handleEditStatic('pi_4')} image={staticBanners['pi_4']?.desktopImg} /></Col>
                                    </Row>
                                </Col>
                            </Row>
                        </div>
                        <div>
                            <Title level={5}>手機版</Title>
                            {renderMobileGrid('pi')}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div style={{ padding: 24, background: '#fff', height: '100%', overflowY: 'auto' }}>
            <Title level={4} style={{ marginBottom: 24 }}>廣告資料設定</Title>
            
            <div style={{ background: '#f5f5f5', padding: 16, marginBottom: 24, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: 8 }}>選擇廣告類型</span>
                    <Select value={adType} onChange={setAdType} style={{ width: 200, marginRight: 24 }}>
                        <Option value="HOME_BANNER">首頁banner</Option>
                        <Option value="MERCHANT_BANNER">商家後台輪播</Option>
                        <Option value="HOME_MAIN">首頁主視覺</Option>
                        <Option value="JET_MAIN">Jet貸主視覺</Option>
                        <Option value="PRODUCT_INS">商品分期主視覺</Option>
                    </Select>
                </div>
                <div>
                    <span style={{ marginRight: 16 }}>發佈時間 : {currentTime}</span>
                    <Button type="primary" onClick={handlePublish}>發布</Button>
                </div>
            </div>

            {renderContent()}

            {/* Sort Modal */}
            <Modal
                title="上傳圖片 (排序)"
                visible={isSortModalVisible}
                onCancel={() => setIsSortModalVisible(false)}
                bodyStyle={{ padding: 24 }}
                footer={[
                    <Button key="cancel" onClick={() => setIsSortModalVisible(false)}>取消</Button>,
                    <Button key="confirm" type="primary" onClick={() => setIsSortModalVisible(false)}>確認</Button>
                ]}
            >
                <SortableList useDragHandle items={banners} onSortEnd={onSortEnd} onClose={handleDelete} />
            </Modal>

            {/* Add/Edit Modal */}
            <Modal
                title="上傳圖片"
                visible={isAddModalVisible}
                onCancel={() => setIsAddModalVisible(false)}
                onOk={handleSave}
                okText="上傳"
                cancelText="取消"
                confirmLoading={isSaving}
                width={600}
                bodyStyle={{ padding: 24 }}
            >
                <Form form={editForm} layout="vertical">
                    <Form.Item label="* 狀態" name="status" initialValue="已發佈">
                        <Input disabled />
                    </Form.Item>
                    
                    {renderUploadItem(`* 電腦版圖片 (建議尺寸 : ${getRecSize(false)})`, "desktopImg")}
                    {renderUploadItem(`* 手機版圖片 (建議尺寸 : ${getRecSize(true)})`, "mobileImg")}

                    <Form.Item label="活動連結" name="link">
                        <Input />
                    </Form.Item>

                    <Form.Item label="替代文字" name="alt">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default RootNewOfficialBanner;
